import type {ComponentProps, FC} from 'react';

import {Heading} from '@/modules/foundation/components/typography/heading';
import {Paragraph} from '@/modules/foundation/components/typography/paragraph';
import {cn} from '@/modules/foundation/shared/tailwind';
import type {CommerceProductColor} from '@/sanity/schemas/types';

import {RadioTileFrame} from '../form/RadioTileFrame';

export type ProductColorConfiguration = CommerceProductColor['_id'];

export type ProductColorSelectorProps = ComponentProps<'fieldset'> & {
	colors: CommerceProductColor[];
	namePrefix?: string;
	defaultConfiguration: ProductColorConfiguration | null;
	legend?: string;
	legendStyle?: 'heading' | 'paragraph';
	disableNoneSelection?: boolean;
	size?: 'small' | 'medium' | 'large';
};

export const ProductColorSelector: FC<ProductColorSelectorProps> = ({
	colors,
	namePrefix,
	defaultConfiguration,
	legend,
	legendStyle = 'heading',
	disableNoneSelection,
	size,
	...rest
}) => {
	const colorsById = colors.reduce((acc, color) => {
		if (color) {
			acc.set(color._id, color);
		}

		return acc;
	}, new Map<string, CommerceProductColor>());

	const uniqueColors = colorsById ? Array.from(colorsById.values()) : [];

	return (
		<fieldset {...rest}>
			{legend && legendStyle === 'heading' && (
				<Heading size="h3" className="mb-2" asChild>
					<legend className="">{legend}</legend>
				</Heading>
			)}
			{legend && legendStyle === 'paragraph' && (
				<Paragraph className="mb-2 font-semibold" asChild>
					<legend className="">{legend}</legend>
				</Paragraph>
			)}
			<div
				className={cn(
					'flex flex-wrap gap-2',
					size === 'small' && 'gap-1',
					size === 'large' && 'gap-5',
				)}
			>
				{!disableNoneSelection && (
					<RadioTileFrame
						name={`${namePrefix}`}
						value={'none'}
						defaultChecked={defaultConfiguration === null}
						labelProps={{
							'aria-label': 'Ikke valgt',
							title: 'Ikke valgt',
						}}
					>
						<div
							className={cn(
								'size-11 bg-white after:absolute after:inset-0 after:flex after:translate-1/3 after:translate-x-1/3 after:-rotate-45 after:border-t-2 after:border-t-red-800 after:content-[""]',
								size === 'small' && 'size-9',
								size === 'large' && 'size-13',
							)}
						></div>
					</RadioTileFrame>
				)}
				{uniqueColors.map((color) => {
					return (
						<RadioTileFrame
							key={color._id}
							name={`${namePrefix}`}
							value={color._id}
							defaultChecked={color._id === defaultConfiguration}
							labelProps={{
								'aria-label': color.name,
								title: color.name,
							}}
						>
							<div
								className={cn(
									'size-11',
									size === 'small' && 'size-9',
									size === 'large' && 'size-13',
								)}
								style={{backgroundColor: color.hex}}
							></div>
						</RadioTileFrame>
					);
				})}
			</div>
		</fieldset>
	);
};
